import { Action, createAction, handleActions } from 'redux-actions'
import { call, put, select, takeLatest, takeLeading } from 'redux-saga/effects'

import {
  addFavoritePayment,
  BILL_PAYMENT_API_URL,
  removeFavoritePayment,
} from '@/api/BillPaymentApi'
import { IEnterTransactionPinForTransfer, ISecureConfirmation } from '@/api/BridgeApi'
import * as Bridge from '@/api/BridgeApi'
import { getCSRF } from '@/api/CsrfApi'
import {
  fetchSecureConfirmMessage,
  IGenerateSecureConfirmationMessageResponse,
} from '@/api/SecureConfirmApi'
import { LEAVE_ALERT_TYPE } from '@/components/common/layer/Leave/LeaveLayer'
import configs from '@/configs'
import {
  BILL_PAYMENT_ROUTES,
  ENTRY_ROUTE_QUERY,
  LOG_EVENT_DISBURSAL_VALUE,
  LOG_EVENT_NAME,
  LOG_EVENT_VALUE,
  OKSign,
  STATUS_CODE,
} from '@/domains/CommonDomain'
import { SecureTransactionType } from '@/domains/SecureConfirmDomain'
import {
  BILLS_IDENTIFIER_TYPE_CODE,
  EmptyBillPaymentPayInfo,
  EmptyBillPaymentPayResponse,
  IBillPaymentPayRequest,
  IBillPaymentPayResponse,
  isBillPaymentTransactionFailedCode,
} from '@/domains/version/V2/BillPaymentDomain'
import { IRootState } from '@/modules'
import { errorActions } from '@/modules/common/error'
import { layerActions } from '@/modules/common/layer'
import { loadingActions } from '@/modules/common/loading'
import { billPaymentAmountSelector } from '@/modules/version/V2/bill-payment/BillPaymentAmountModule'
import {
  billMainActions,
  billPaymentMainSelector,
  IFlattenProductType,
} from '@/modules/version/V2/bill-payment/BillPaymentMainModule'
import { createPostAction } from '@/utils/actionUtils'
import { BRIDGE_ERROR_CODE } from '@/utils/bridgeUtils/constants'
import {
  HostType,
  IRequestPinAuthResult,
  RequestPinAuthAction,
} from '@/utils/bridgeUtils/features/requestPinAuth'
import { newError } from '@/utils/errorUtils'
import { getFormattedCardNo } from '@/utils/formatUtils'
import { push } from '@/utils/historyUtils'
import { $ } from '@/utils/i18nUtils'
import { toInteger } from '@/utils/numberUtils'
import { apiFailureReducer, reset } from '@/utils/reducerUtils'
import af from '@/utils/trackingUtils'

export const BILL_PAYMENT_RESULT = 'BillPaymentResult'

const BILL_PAYMENT_PAY = `${BILL_PAYMENT_RESULT}/billPaymentPay`
const BILL_PAYMENT_PAY_V3 = `${BILL_PAYMENT_RESULT}/billPaymentPay/v3`
const BILL_PAYMENT_RESULT_FAVORITE_ADD = `${BILL_PAYMENT_RESULT}/favorite/add`
const BILL_PAYMENT_RESULT_FAVORITE_REMOVE = `${BILL_PAYMENT_RESULT}/favorite/remove`
const CLEAR_STATE = `${BILL_PAYMENT_RESULT}/clearState`
const REDIRECT_TO = `${BILL_PAYMENT_RESULT}/redirectTo`
const SET_PAY_INFO = `${BILL_PAYMENT_RESULT}/setPayInfo`

export interface IPayAction {
  amount: string
  fee: string
  customerId: string
  reserved: string
  productTypeCode: string

  isDisbursal: string
  loanAcctNo: string
  loanAmt: string
}

export interface IDisbursalInfo {
  acctNo: string
  acctTitle: string
  loanAmt: string
  loanBalance: string
  creditLimit: string
}
const emptyDisbursalInfo: IDisbursalInfo = {
  acctNo: '',
  acctTitle: '',
  creditLimit: '',
  loanAmt: '',
  loanBalance: '',
}

export interface IBillPaymentResultState {
  payResponse: IBillPaymentPayResponse | null
  payInfo: IPayAction | null
}

export const initialState: IBillPaymentResultState = {
  payResponse: null,
  payInfo: null,
}

export const billResultActions = {
  clearState: createAction(CLEAR_STATE),
  redirectTo: createAction<string>(REDIRECT_TO),
  setPayInfo: createAction<IPayAction>(SET_PAY_INFO),
}

export const payAsync = createPostAction<IPayAction>(BILL_PAYMENT_PAY)
export const payAsyncV3 = createPostAction(BILL_PAYMENT_PAY_V3)
export const billResultFavoriteAddAsync = createPostAction(BILL_PAYMENT_RESULT_FAVORITE_ADD)
export const billResultFavoriteRemoveAsync = createPostAction(BILL_PAYMENT_RESULT_FAVORITE_REMOVE)

const payReducer = (state: IBillPaymentResultState, action: Action<any>) => ({
  ...state,
  payResponse: action.payload,
})

const reducer = {
  [payAsync.SUCCESS]: payReducer,
  [payAsync.FAILURE]: payReducer,
  [billResultFavoriteAddAsync.SUCCESS]: (state: IBillPaymentResultState) => ({
    ...state,
    payResponse: {
      ...state.payResponse,
      favorite: true,
    },
  }),
  [billResultFavoriteRemoveAsync.SUCCESS]: (state: IBillPaymentResultState) => ({
    ...state,
    payResponse: {
      ...state.payResponse,
      favorite: false,
    },
  }),
  [billResultFavoriteRemoveAsync.FAILURE]: apiFailureReducer,
  [CLEAR_STATE]: (state: IBillPaymentResultState) => reset(state, initialState),
  [SET_PAY_INFO]: (state: IBillPaymentResultState, action: Action<IPayAction>) => ({
    ...state,
    payInfo: action.payload,
  }),
}

export const billPaymentResultSelector = {
  payResponse: (state: IRootState): IBillPaymentPayResponse =>
    state[BILL_PAYMENT_RESULT].payResponse ?? EmptyBillPaymentPayResponse,
  payInfo: (state: IRootState): IPayAction =>
    state[BILL_PAYMENT_RESULT].payInfo ?? EmptyBillPaymentPayInfo,
  isFavorite: (state: IRootState): boolean => billPaymentResultSelector.payResponse(state).favorite,
  isDisbursal: (state: IRootState): boolean =>
    billPaymentResultSelector.payInfo(state).isDisbursal === OKSign.YES,
  disbursalInfo: (state: IRootState): IDisbursalInfo => {
    const {
      acctNo,
      acctTitle,
      creditLimit,
      loanAmt,
      loanBalance,
    } = billPaymentResultSelector.payResponse(state)

    return {
      acctNo,
      acctTitle,
      creditLimit: creditLimit ?? emptyDisbursalInfo.creditLimit,
      loanAmt: loanAmt ?? emptyDisbursalInfo.loanAmt,
      loanBalance: loanBalance ?? emptyDisbursalInfo.loanBalance,
    }
  },
}

export const billPaymentResultReducer = handleActions(reducer, initialState)

export function* payV2(action: Action<IPayAction | undefined>) {
  try {
    yield put(loadingActions.start(BILL_PAYMENT_RESULT))

    /* set pay info
     * Saga 에서 저장하거나
     * Action dispatch 전에 저장 */
    if (action.payload) {
      yield put(billResultActions.setPayInfo(action.payload))
    }

    const payRequestParams: IBillPaymentPayRequest = yield call(buildPayRequestParams)
    const secureConfirmation: ISecureConfirmation = yield call(
      buildSecureConfirmationParams,
      payRequestParams,
    )

    const trxPinForTransferPayload: IEnterTransactionPinForTransfer = {
      hostType: HostType.BANK_API,
      apiUrl: BILL_PAYMENT_API_URL.BILL_PAY,
      request: {
        secureConfirmation,
        ...payRequestParams,
      },
    }

    // af.send
    if (parseInt(payRequestParams.loanAmt as string) > 0) {
      // 대출 하는 경우
      af.send(LOG_EVENT_NAME.APP_QCREDIT_DISBURSAL_ENTERPIN, {
        disbursaltype: LOG_EVENT_DISBURSAL_VALUE.DISBURSAL_TYPE,
      })
    }

    const { result }: IRequestPinAuthResult = yield call(
      Bridge.enterTransactionPinForTransferBeta,
      trxPinForTransferPayload,
    )
    console.log('resultmoudle result :::::::', result)
    if (!result) {
      throw newError(STATUS_CODE.UNKNOWN_CLIENT_ERROR)
    }
    if (result.action === RequestPinAuthAction.CLOSE) {
      return
    }
    if (
      result.action === RequestPinAuthAction.AUTH_FAIL ||
      result.action === RequestPinAuthAction.AUTH_ERROR ||
      result.action !== RequestPinAuthAction.AUTH_SUCCESS
    ) {
      const { code, message } = JSON.parse(result.response)
      const err = {
        code: toInteger(code),
        message: message,
      }
      throw err
    }
    console.warn('requstPinAuth::response', JSON.parse(result.response))
    const billPaymentPayResponse: IBillPaymentPayResponse = JSON.parse(result.response)
    yield put(payAsync.success(billPaymentPayResponse))

    // af.send 시작
    const mainStarttype: string = yield select(billPaymentMainSelector.getStarttype)
    const { paymentTypeCode, productappsBillType: billtype }: IFlattenProductType = yield select(
      billPaymentMainSelector.flattenProductType,
      payRequestParams.productTypeCode,
    )
    const starttype =
      mainStarttype === ''
        ? paymentTypeCode === '01'
          ? LOG_EVENT_VALUE.BILL_PAYMENT.START_TYPE.PULSA
          : paymentTypeCode === '02'
          ? LOG_EVENT_VALUE.BILL_PAYMENT.START_TYPE.EWALLET
          : LOG_EVENT_VALUE.BILL_PAYMENT.START_TYPE.BILLS
        : mainStarttype
    af.send(LOG_EVENT_NAME.APP_BILLPAYMENT_SUCCESS, { starttype, billtype })

    // FAVORITES, RECENTLY 값 초기화
    yield put(billMainActions.clearStarttype())
    // af.send 종료

    yield call(
      push,
      `${BILL_PAYMENT_ROUTES.RESULT}/${payRequestParams.productTypeCode}?${ENTRY_ROUTE_QUERY}`,
    )
  } catch (e) {
    const errorObj: any = e
    if (errorObj.code === BRIDGE_ERROR_CODE.SECURE_CONFIRMATION_CANCELED) {
      return
    } else if (errorObj.code === STATUS_CODE.BILL_PAYMENT_TRANSACTION_FAIL) {
      // 청구서가 유효하지 않았다면
      // 청구서가 유효하지 않음을 알리는 경고 레이어를 표시
      yield put(layerActions.open(LEAVE_ALERT_TYPE.BILL_PAYMENT_TRANSACTION_FAIL))
    } else if (errorObj.code === STATUS_CODE.INVALID_ACCCOUNT_STATUS) {
      // 이체 중에 계좌의 상태 이상을 발견했다면
      // 계좌의 상태가 유효하지 않음을 알리는 경고 레이어를 표시
      yield put(layerActions.open(LEAVE_ALERT_TYPE.BILL_PAYMENT_INVALID_ACCCOUNT_STATUS))
    } else if (errorObj.code === STATUS_CODE.INSUFFICIENT_BALANCE) {
      // 이체 중에 계좌의 잔고 부족을 발견했다면
      // 계좌의 잔고 부족을 알리는 경고 레이어를 표시
      yield put(layerActions.open(LEAVE_ALERT_TYPE.BILL_PAYMENT_INSUFFICIENT_BALANCE))
    } else {
      yield put(errorActions.set(errorObj))
    }
  } finally {
    yield put(loadingActions.finish(BILL_PAYMENT_RESULT))
  }
}

/* Bill Payment V3 pay logic */
function* payV3() {
  try {
    yield put(loadingActions.start(BILL_PAYMENT_RESULT))

    /* create finRequestPinAuth parameter */
    const payRequestParams: IBillPaymentPayRequest = yield call(buildPayRequestParams)
    const secureConfirmation: ISecureConfirmation = yield call(
      buildSecureConfirmationParams,
      payRequestParams,
    )
    const trxPinForTransferPayload: IEnterTransactionPinForTransfer = {
      hostType: HostType.BANK_API,
      apiUrl: BILL_PAYMENT_API_URL.BILL_PAY,
      request: {
        secureConfirmation,
        ...payRequestParams,
      },
    }

    /* AppsFlyer */
    const loanAmt = parseInt(payRequestParams.loanAmt as string)
    if (loanAmt > 0) {
      // 대출 하는 경우
      af.send(LOG_EVENT_NAME.APP_QCREDIT_DISBURSAL_ENTERPIN, {
        disbursaltype: LOG_EVENT_DISBURSAL_VALUE.DISBURSAL_TYPE,
      })
    }

    /* request finRequestPinAuth */
    const { result }: IRequestPinAuthResult = yield call(
      Bridge.enterTransactionPinForTransferBeta,
      trxPinForTransferPayload,
    )
    if (!result) {
      throw newError(STATUS_CODE.UNKNOWN_CLIENT_ERROR)
    }
    if (result.action === RequestPinAuthAction.CLOSE) {
      return
    }
    if (
      result.action === RequestPinAuthAction.AUTH_FAIL ||
      result.action === RequestPinAuthAction.AUTH_ERROR ||
      result.action !== RequestPinAuthAction.AUTH_SUCCESS
    ) {
      const { code, message, content } = JSON.parse(result.response)
      if (isBillPaymentTransactionFailedCode(code) && content) {
        yield put(payAsync.failure(JSON.parse(content)))
      }
      const err = {
        code: toInteger(code),
        message: message,
      }
      throw err
    }
    const billPaymentPayResponse: IBillPaymentPayResponse = JSON.parse(result.response)

    const formattedCustomerId =
      billPaymentPayResponse.identifierType === BILLS_IDENTIFIER_TYPE_CODE.CREDITCARD
        ? getFormattedCardNo(billPaymentPayResponse.customerId)
        : billPaymentPayResponse.customerId

    yield put(
      payAsync.success({
        ...billPaymentPayResponse,
        customerId: formattedCustomerId,
      } as IBillPaymentPayResponse),
    )

    /* AppsFlyer */
    const mainStarttype: string = yield select(billPaymentMainSelector.getStarttype)
    const { paymentTypeCode, productappsBillType: billtype }: IFlattenProductType = yield select(
      billPaymentMainSelector.flattenProductType,
      payRequestParams.productTypeCode,
    )
    const starttype =
      mainStarttype === ''
        ? paymentTypeCode === '01'
          ? LOG_EVENT_VALUE.BILL_PAYMENT.START_TYPE.PULSA
          : paymentTypeCode === '02'
          ? LOG_EVENT_VALUE.BILL_PAYMENT.START_TYPE.EWALLET
          : LOG_EVENT_VALUE.BILL_PAYMENT.START_TYPE.BILLS
        : mainStarttype

    af.send(LOG_EVENT_NAME.APP_BILLPAYMENT_SUCCESS, { starttype, billtype })

    /* FAVORITES, RECENTLY 값 초기화 */
    yield put(billMainActions.clearStarttype())

    /* pay 성공 후 Result page 로 이동 */
    yield call(
      push,
      `${BILL_PAYMENT_ROUTES.RESULT}/${payRequestParams.productTypeCode}?${ENTRY_ROUTE_QUERY}`,
    )
  } catch (e) {
    const errorObj: any = e
    if (errorObj.code === BRIDGE_ERROR_CODE.SECURE_CONFIRMATION_CANCELED) {
      return
    }
    const isDisbursal: boolean = yield select(billPaymentResultSelector.isDisbursal)
    if (isDisbursal) {
      if(errorObj.code === STATUS_CODE.BILL_PAYMENT_TRANSACTION_PENDING) {
        yield put(layerActions.open(LEAVE_ALERT_TYPE.BILL_PAYMENT_TRANSACTION_PENDING))
      } else if(isBillPaymentTransactionFailedCode(errorObj.code)) {
        yield put(layerActions.open(LEAVE_ALERT_TYPE.BILL_PAYMENT_QUICK_CREDIT_DISBURSED))
      } else {
        yield put(layerActions.open(LEAVE_ALERT_TYPE.BILL_PAYMENT_QUICK_CREDIT_DISBURSEMENT_FAILED))
      }
    } else if (errorObj.code === STATUS_CODE.BILL_PAYMENT_TRANSACTION_FAIL) {
      // 청구서가 유효하지 않았다면
      // 청구서가 유효하지 않음을 알리는 경고 레이어를 표시
      yield put(layerActions.open(LEAVE_ALERT_TYPE.BILL_PAYMENT_TRANSACTION_FAIL))
    } else if (errorObj.code === STATUS_CODE.INVALID_ACCCOUNT_STATUS) {
      // 이체 중에 계좌의 상태 이상을 발견했다면
      // 계좌의 상태가 유효하지 않음을 알리는 경고 레이어를 표시
      yield put(layerActions.open(LEAVE_ALERT_TYPE.BILL_PAYMENT_INVALID_ACCCOUNT_STATUS))
    } else if (errorObj.code === STATUS_CODE.INSUFFICIENT_BALANCE) {
      // 이체 중에 계좌의 잔고 부족을 발견했다면
      // 계좌의 잔고 부족을 알리는 경고 레이어를 표시
      yield put(layerActions.open(LEAVE_ALERT_TYPE.BILL_PAYMENT_INSUFFICIENT_BALANCE))
    } else {
      yield put(errorActions.set(errorObj))
    }
  } finally {
    yield put(loadingActions.finish(BILL_PAYMENT_RESULT))
  }
}

function* buildPayRequestParams() {
  const { isDisbursal: quickCreditDisbursalYn, ...payInfo }: IPayAction = yield select(
    billPaymentResultSelector.payInfo,
  )
  const { productTypeSwitchId, productTypeIdentifierType }: IFlattenProductType = yield select(
    billPaymentMainSelector.flattenProductType,
    payInfo.productTypeCode,
  )
  const { acctNo } = yield select(billPaymentAmountSelector.selectedAccount)
  const payRequestParams: IBillPaymentPayRequest = {
    ...payInfo,
    acctNo,
    customerId: payInfo.customerId.split('-').join(''),
    productTypeSwitchId,
    identifierType: productTypeIdentifierType,
    quickCreditDisbursalYn,
  }

  return payRequestParams
}

function* buildSecureConfirmationParams(payRequestPayload: IBillPaymentPayRequest) {
  try {
    const secureTransaction = SecureTransactionType.BILL_PAYMENT_PAY_BETA
    const secureConfirmationMessage: IGenerateSecureConfirmationMessageResponse = yield call(
      fetchSecureConfirmMessage,
      secureTransaction,
      payRequestPayload,
    )
    const secureConfirmationParams: ISecureConfirmation = {
      ...secureConfirmationMessage,
      secureTransaction,
    }

    return secureConfirmationParams
  } catch (e) {
    yield put(errorActions.set(e))
  }
}

export function* addFavoriteAtBillPaymentResult() {
  try {
    yield put(loadingActions.start(BILL_PAYMENT_RESULT))
    yield call(getCSRF)
    const { trxPayKey } = yield select(billPaymentResultSelector.payResponse)

    yield call(addFavoritePayment, { trxPayKey })
    yield put(billResultFavoriteAddAsync.success())
  } catch (e) {
    const errorObj: any = e
    switch (errorObj.code) {
      case STATUS_CODE.MAXIMUM_FAVORITE_COUNT_OVER:
        yield call(Bridge.showNotification, {
          message: $('billpayment_main_favorite_exceederror_toast'),
        })
        break

      default:
        yield put(errorActions.set(errorObj))
        break
    }
  } finally {
    yield put(loadingActions.finish(BILL_PAYMENT_RESULT))
  }
}

export function* removeFavoriteAtBillPaymentResult() {
  try {
    yield put(loadingActions.start(BILL_PAYMENT_RESULT))
    yield call(getCSRF)

    const { trxPayKey } = yield select(billPaymentResultSelector.payResponse)

    yield call(removeFavoritePayment, { trxPayKey })
    yield put(billResultFavoriteRemoveAsync.success())
  } catch (e) {
    const errorObj: any = e
    yield put(errorActions.set(errorObj))
  } finally {
    yield put(loadingActions.finish(BILL_PAYMENT_RESULT))
  }
}

export function* redirectTo(action: Action<string>) {
  const landingScheme = action.payload
  // const { isDebitCard, isSchedulePayment, isBillPayment, route } = landingRoute(landingScheme)

  if (!landingScheme) {
    return
  }

  // if (isDebitCard || isSchedulePayment || isBillPayment) {
  //   yield call(redirect, `${route}?${ENTRY_ROUTE_QUERY}`)

  //   return
  // }

  yield call(Bridge.navigateTo, `${configs.APP_SCHEME}${landingScheme.substr(1)}`, false)
}

export const billPaymentResultSaga = [
  takeLeading(payAsync.POST, payV2),
  takeLeading(payAsyncV3.POST, payV3),
  takeLatest(billResultFavoriteAddAsync.POST, addFavoriteAtBillPaymentResult),
  takeLatest(billResultFavoriteRemoveAsync.POST, removeFavoriteAtBillPaymentResult),
  takeLatest(REDIRECT_TO, redirectTo),
]
